/**
 * Offline-aware Orders Hook
 * Uses SQLite when offline in Electron, falls back to Supabase when online
 * Handles offline order creation and syncing
 */

import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useNetwork } from '@/contexts/NetworkContext';
import { useElectron } from '@/hooks/useElectron';
import { useOrders } from '@/hooks/useOrders';
import {
  OfflineOrderInput,
  prepareOfflineOrder,
  validateOfflineOrder,
} from '@/services/offlineOrderService';
import type { Order } from '@/types/index';

// Type for create order result - using Partial<Order> to be flexible with offline/online differences
interface CreateOrderResult {
  success: boolean;
  order?: Partial<Order> & { id: string; total_amount: number; status: string };
  error?: string;
  warnings?: string[];
  isOffline?: boolean;
}

interface OfflineOrdersResult {
  orders: Order[];
  loading: boolean;
  error: string | null;
  isOffline: boolean;
  isFromLocalDb: boolean;
  pendingSyncCount: number;
  refetch: () => Promise<void>;
  createOrder: (orderData: OfflineOrderInput) => Promise<CreateOrderResult>;
  updateOrderStatus: (orderId: string, status: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  isOrderUpdating: (orderId: string) => boolean;
  clearOptimisticUpdate: (orderId: string) => void;
}

// Type for SQLite branch data
interface SqliteBranch {
  tax_rate?: number;
  service_charge_rate?: number;
}

// Type for SQLite order creation result
interface SqliteOrderResult {
  order?: {
    id: string;
    order_number: string;
  };
}

export function useOfflineOrders(
  branchId?: string,
  options?: { lean?: boolean }
): OfflineOrdersResult {
  const { isOnline, isElectronApp, pendingSyncCount } = useNetwork();
  const { database, pullOrders } = useElectron();
  
  // Determine if we should use offline mode
  // Must be calculated BEFORE calling useOrders so we can disable network requests
  const shouldUseOffline = isElectronApp && !isOnline && !!database;
  
  // State for offline data
  const [offlineOrders, setOfflineOrders] = useState<Order[]>([]);
  const [offlineLoading, setOfflineLoading] = useState(false);
  const [offlineError, setOfflineError] = useState<string | null>(null);
  
  // Use the standard hook for online mode - DISABLED when offline to prevent network errors
  const onlineResult = useOrders(branchId, { ...options, enabled: !shouldUseOffline });
  
  // Fetch orders from local SQLite database with items
  const fetchOfflineOrders = useCallback(async () => {
    if (!branchId || !database) {
      setOfflineOrders([]);
      return;
    }
    
    setOfflineLoading(true);
    setOfflineError(null);
    
    try {
      // Use getOrdersWithItems to get orders with embedded items
      // Falls back to getOrders if getOrdersWithItems not available
      const getOrdersFunc = (database as { getOrdersWithItems?: typeof database.getOrders }).getOrdersWithItems || database.getOrders;
      const data = await getOrdersFunc(branchId, {
        limit: options?.lean ? 50 : 100,
      });
      
      // Transform SQLite data to match Order interface
      const orders = (data || []).map((row: Record<string, unknown>) => {
        // Transform items from SQLite format to Order format
        const rawItems = (row.items as Array<Record<string, unknown>>) || [];
        const transformedItems = rawItems.map((item: Record<string, unknown>) => ({
          id: item.id as string,
          order_id: item.order_id as string,
          branch_id: row.branch_id as string, // Use order's branch_id
          menu_item_id: item.menu_item_id as string,
          menu_item_variant_id: item.variant_id as string | null,
          quantity: item.quantity as number,
          unit_price: item.unit_price as number,
          total_price: item.total_price as number,
          notes: item.notes as string | null,
          special_instructions: null,
          customizations: item.modifiers as string | null,
          is_acknowledged: true,
          is_removed: false,
          created_at: item.created_at as string,
          updated_at: item.updated_at as string,
          // Name for display - both direct and nested for compatibility
          name: item.name as string,
          // Add menu_items object for components that expect nested data (like OrderCard)
          menu_items: {
            id: item.menu_item_id as string,
            name: item.name as string,
          },
        }));
        
        return {
          id: row.id as string,
          branch_id: row.branch_id as string,
          order_number: row.order_number as number,
          customer_name: row.customer_name as string | null,
          customer_phone: row.customer_phone as string | null,
          customer_email: row.customer_email as string | null,
          customer_address: row.customer_address as string | null,
          customer_id: row.customer_id as string | null,
          table_number: row.table_number as string | null,
          order_type: row.order_type as string,
          status: row.status as string,
          payment_status: row.payment_status as string | null,
          payment_method: row.payment_method as string | null,
          subtotal_amount: row.subtotal as number,
          tax_amount: row.tax_amount as number,
          service_amount: row.service_charge as number,
          total_amount: row.total as number,
          notes: row.notes as string | null,
          delivery_fee: row.delivery_fee as number | null,
          delivery_zone_id: null,
          estimated_delivery_time: null,
          table_session_id: null,
          created_at: row.created_at as string,
          updated_at: row.updated_at as string,
          completed_at: row.completed_at as string | null,
          // Mark offline-created orders
          _isOfflineCreated: row.is_created_offline === 1,
          _isSynced: row.is_synced === 1,
          // Order items from the embedded items array
          order_items: transformedItems,
        };
      }) as unknown as Order[];
      
      setOfflineOrders(orders);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load offline orders';
      setOfflineError(errorMessage);
      console.error('[OfflineOrders] Error fetching from SQLite:', err);
    } finally {
      setOfflineLoading(false);
    }
  }, [branchId, database, options?.lean]);
  
  // Fetch offline data when needed
  useEffect(() => {
    if (shouldUseOffline) {
      fetchOfflineOrders();
    }
  }, [shouldUseOffline, fetchOfflineOrders]);
  
  // Create order - handles both online and offline modes
  const createOrder = useCallback(async (orderData: OfflineOrderInput) => {
    // Validate order data
    const validation = validateOfflineOrder(orderData);
    if (!validation.valid) {
      return {
        success: false,
        error: validation.errors.join(', '),
        warnings: validation.warnings,
      };
    }
    
    // If offline and in Electron, create locally
    if (shouldUseOffline && database) {
      try {
        console.log('[OfflineOrders] Creating order offline');

        // Guard: ensure menu cache exists before allowing offline ordering
        const [categories, items] = await Promise.all([
          database.getCategories(orderData.branchId),
          database.getAllMenuItems(orderData.branchId),
        ]);

        if (!categories?.length || !items?.length) {
          return {
            success: false,
            error: 'offline.menu.notCached',
            isOffline: true,
          };
        }
        
        // Get branch settings for tax calculation (if cached)
        let branchSettings: { taxRate?: number; serviceChargeRate?: number } | undefined;
        try {
          const branch = await database.getBranch(orderData.branchId) as SqliteBranch | null;
          if (branch) {
            branchSettings = {
              taxRate: branch.tax_rate || 0,
              serviceChargeRate: branch.service_charge_rate || 0,
            };
          }
        } catch {
          console.warn('[OfflineOrders] Could not fetch branch settings, using defaults');
        }
        
        // Prepare the order for SQLite
        const { order: orderPayload, items: itemsPayload } = prepareOfflineOrder(
          orderData,
          branchSettings
        );
        
        // Create in SQLite via IPC
        const result = await database.createOrder({
          branch_id: orderPayload.branch_id,
          customer_name: orderPayload.customer_name,
          customer_phone: orderPayload.customer_phone,
          table_number: orderPayload.table_number,
          order_type: orderPayload.order_type,
          notes: orderPayload.notes,
          items: orderData.items.map(item => ({
            menu_item_id: item.menuItemId,
            variant_id: item.variantId,
            name: item.name,
            quantity: item.quantity,
            unit_price: item.unitPrice,
            notes: item.notes,
          })),
        }) as SqliteOrderResult;
        
        // Refetch to update the list
        await fetchOfflineOrders();
        
        return {
          success: true,
          order: {
            id: String(result?.order?.id || orderPayload.id),
            order_number: result?.order?.order_number,
            total_amount: orderPayload.total as number,
            status: 'pending',
          },
          warnings: validation.warnings,
          isOffline: true,
        };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create offline order';
        console.error('[OfflineOrders] Error creating offline order:', err);
        return {
          success: false,
          error: errorMessage,
          warnings: validation.warnings,
        };
      }
    }
    
    // Online mode - use the standard order creation
    try {
      const result = await onlineResult.createOrder({
        branchId: orderData.branchId,
        customerName: orderData.customerName || 'Walk-in Customer',
        customerPhone: orderData.customerPhone || '',
        customerEmail: orderData.customerEmail,
        customerAddress: orderData.customerAddress,
        notes: orderData.notes,
        orderType: orderData.orderType,
        tableNumber: orderData.tableNumber,
        deliveryZoneId: orderData.deliveryZoneId,
        deliveryFee: orderData.deliveryFee,
        items: orderData.items.map(item => ({
          menuItemId: item.menuItemId,
          variantId: item.variantId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          notes: item.notes,
        })),
        customerId: orderData.customerId || '',
      });
      
      return {
        success: result.success,
        order: result.order,
        error: result.error,
        isOffline: false,
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create order';
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, [shouldUseOffline, database, onlineResult, fetchOfflineOrders]);
  
  // Update order status - handles both online and offline modes
  // In Electron mode, ALWAYS use local database for status updates
  // This ensures we use the correct local ID and sync handles server ID resolution
  const updateOrderStatus = useCallback(async (orderId: string, status: string) => {
    // In Electron app, always update locally - sync engine will handle server sync
    if (isElectronApp && database) {
      try {
        console.log('[OfflineOrders] Updating order status via local database:', orderId, status);
        await database.updateOrderStatus(orderId, status);
        
        // Refetch to update the list
        await fetchOfflineOrders();
        
        return { success: true };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update order status';
        console.error('[OfflineOrders] Error updating order status:', err);
        return { success: false, error: errorMessage };
      }
    }
    
    // Non-Electron mode (web browser) - use the standard online status update
    return onlineResult.updateOrderStatus(orderId, status);
  }, [isElectronApp, database, onlineResult, fetchOfflineOrders]);
  
  // Helper functions for offline mode - no-op implementations
  const offlineIsOrderUpdating = useCallback((_orderId: string) => false, []);
  const offlineClearOptimisticUpdate = useCallback((_orderId: string) => {}, []);
  
  // In Electron mode, always use local SQLite as source of truth
  // This prevents status changes from being overwritten by stale Supabase cache
  const mergedOrders = useMemo(() => {
    // If we're in Electron app, always prefer local SQLite data
    // This ensures offline status changes are always visible
    if (isElectronApp && offlineOrders.length > 0) {
      console.log('[OfflineOrders] Using local SQLite as source of truth:', {
        localCount: offlineOrders.length,
        isOnline,
        pendingSyncCount
      });
      return offlineOrders;
    }
    
    // If we're in strict offline mode (not Electron), just use offline orders
    if (shouldUseOffline) {
      return offlineOrders;
    }
    
    const onlineOrders = onlineResult.orders || [];
    
    // If there are pending sync items and we have local data, merge them
    // This ensures orders don't disappear and local changes are preserved while syncing
    if (pendingSyncCount > 0 && offlineOrders.length > 0) {
      // Create maps for quick lookup
      const onlineOrderMap = new Map(onlineOrders.map(o => [o.id, o]));
      const localOrderMap = new Map(offlineOrders.map(o => [o.id, o]));
      
      // Start with online orders, but override with local changes where applicable
      const mergedMap = new Map<string, Order>();
      
      // First, add all online orders
      for (const onlineOrder of onlineOrders) {
        const localOrder = localOrderMap.get(onlineOrder.id);
        
        // If local version exists and is unsynced, use local status/data
        if (localOrder) {
          const isLocalUnsynced = (localOrder as unknown as { _isSynced?: boolean })._isSynced === false;
          const localUpdatedAt = new Date(localOrder.updated_at || 0).getTime();
          const onlineUpdatedAt = new Date(onlineOrder.updated_at || 0).getTime();
          
          // Preserve local status if:
          // 1. Local order is marked as unsynced, OR
          // 2. Local order has a more recent update time
          if (isLocalUnsynced || localUpdatedAt > onlineUpdatedAt) {
            console.log(`[OfflineOrders] Preserving local status for order ${onlineOrder.id}:`, {
              localStatus: localOrder.status,
              serverStatus: onlineOrder.status,
              localUpdatedAt,
              onlineUpdatedAt,
              isLocalUnsynced
            });
            // Merge: keep most online data but override status and updated_at from local
            mergedMap.set(onlineOrder.id, {
              ...onlineOrder,
              status: localOrder.status,
              updated_at: localOrder.updated_at,
            } as Order);
          } else {
            mergedMap.set(onlineOrder.id, onlineOrder);
          }
        } else {
          mergedMap.set(onlineOrder.id, onlineOrder);
        }
      }
      
      // Then, add local-only orders (new orders created offline)
      for (const localOrder of offlineOrders) {
        if (!onlineOrderMap.has(localOrder.id)) {
          const isLocalId = typeof localOrder.id === 'string' && localOrder.id.startsWith('local_');
          const isUnsynced = (localOrder as unknown as { _isSynced?: boolean })._isSynced === false;
          
          if (isLocalId || isUnsynced) {
            mergedMap.set(localOrder.id, localOrder);
          }
        }
      }
      
      // Convert to array and sort by created_at descending
      const merged = Array.from(mergedMap.values()).sort((a, b) => {
        const dateA = new Date(a.created_at || 0).getTime();
        const dateB = new Date(b.created_at || 0).getTime();
        return dateB - dateA;
      });
      
      console.log('[OfflineOrders] Merging orders during sync:', {
        onlineCount: onlineOrders.length,
        localCount: offlineOrders.length,
        mergedCount: merged.length,
        pendingSyncCount
      });
      
      return merged;
    }
    
    // Default: use online orders
    return onlineOrders;
  }, [shouldUseOffline, offlineOrders, onlineResult.orders, pendingSyncCount, isElectronApp, isOnline]);
  
  // In Electron app, always fetch from local SQLite as source of truth
  // This ensures local status changes are always visible
  const hasFetchedOfflineRef = useRef(false);
  useEffect(() => {
    if (isElectronApp && database && !hasFetchedOfflineRef.current) {
      hasFetchedOfflineRef.current = true;
      fetchOfflineOrders();
    }
  }, [isElectronApp, database, fetchOfflineOrders]);
  
  // Periodically refresh local orders in Electron mode to catch sync updates
  // Also pull updates from server when online
  useEffect(() => {
    if (!isElectronApp || !database || !branchId) return;
    
    const interval = setInterval(async () => {
      // If online, pull order updates from server first
      if (isOnline && pullOrders) {
        try {
          await pullOrders(branchId);
        } catch (err) {
          console.warn('[OfflineOrders] Failed to pull order updates:', err);
        }
      }
      
      // Then refresh local orders
      fetchOfflineOrders();
    }, 5000); // Refresh every 5 seconds
    
    return () => clearInterval(interval);
  }, [isElectronApp, database, branchId, isOnline, pullOrders, fetchOfflineOrders]);
  
  // Memoized refetch function for online mode that fetches both local and online
  const onlineRefetch = onlineResult.refetch;
  const combinedRefetch = useCallback(async () => {
    await fetchOfflineOrders();
    await onlineRefetch();
  }, [fetchOfflineOrders, onlineRefetch]);
  
  // Return appropriate result based on mode
  if (shouldUseOffline) {
    return {
      orders: offlineOrders,
      loading: offlineLoading,
      error: offlineError,
      isOffline: true,
      isFromLocalDb: true,
      pendingSyncCount,
      refetch: fetchOfflineOrders,
      createOrder,
      updateOrderStatus,
      isOrderUpdating: offlineIsOrderUpdating,
      clearOptimisticUpdate: offlineClearOptimisticUpdate,
    };
  }
  
  return {
    orders: mergedOrders,
    loading: onlineResult.loading,
    error: onlineResult.error,
    isOffline: false,
    isFromLocalDb: pendingSyncCount > 0 && offlineOrders.length > 0,
    pendingSyncCount,
    refetch: combinedRefetch,
    createOrder,
    updateOrderStatus,
    isOrderUpdating: onlineResult.isOrderUpdating,
    clearOptimisticUpdate: onlineResult.clearOptimisticUpdate,
  };
}

export default useOfflineOrders;
